import React, { useState } from 'react';
import ReactDom from 'react-dom';
import { Button } from 'lib/components';
import CustomCostSelect from './CustomCostSelect';
import { CustomCost, JobCustomCost } from 'types';
import classes from '../index.module.scss';

/**
 * Props interface for the CustomCostInputModal component
 */
interface Props {
	/** Array of available custom cost items to select from */
	items: Array<CustomCost>;
	/** Whether the modal is currently open */
	isOpen: boolean;
	/** Function to set the modal's open state */
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Callback function when the modal is closed */
	onClose: () => void;
	/** Callback function to add a new custom cost */
	addCustomCost: (customCost: JobCustomCost) => void;
	/** Array of custom cost IDs that are already selected */
	selected: Array<number>;
}

/**
 * CustomCostInputModal component
 * Provides a modal dialog for adding custom costs to a job
 * Allows selecting a custom cost from available options
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const CustomCostInputModal = ({ items, selected, isOpen, setIsOpen, addCustomCost, onClose }: Props) => {
	// State for the selected custom cost ID
	const [customCostId, setCustomCostId] = useState<number | undefined>();
	// State for the custom cost name
	const [name, setName] = useState<string | undefined>();

	// Don't render anything if the modal is closed
	if (!isOpen) {
		return null;
	}

	// Render the modal using a portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['add-service-modal']}>
			<div className={classes['add-service-modal-content']}>
				{/* Custom cost selector area */}
				<div className={classes['add-service-modal-content--main']}>
					{/* Custom cost dropdown selector */}
					<CustomCostSelect
						items={items}
						selected={selected}
						value={customCostId}
						setValue={setCustomCostId}
						setLabel={setName}
					/>
				</div>
				{/* Action buttons */}
				<div className={classes['add-service-modal-content--footer']}>
					{/* Add Cost button */}
					<Button
						variant="standard"
						title={'Add Cost'}
						onClick={() => {
							if (name && customCostId) {
								// Add the selected custom cost
								addCustomCost({ customCostId, name });
								// Reset form values and close modal
								setCustomCostId(undefined);
								setName(undefined);
								setIsOpen(false);
							}
						}}
						// Disable button if any required field is empty
						disabled={!(name && customCostId)}
					/>
					{/* Cancel button */}
					<Button color="gray" variant="standard" title="Cancel" onClick={onClose} />
				</div>
			</div>
		</div>,
		// Mount the portal in the designated element in the DOM
		document.getElementById('portal') as HTMLElement
	);
};

export default CustomCostInputModal;
